import type { LessonImpulseCodeName, LessonImpulseSummary } from './lesson-impulse'

export interface LessonImpulseFilters {
  kompetenzIds: string[]
  grades: number[]
  subjectCodes: string[]
  bookmarkedOnly: boolean
}

export interface LessonImpulseFilterKompetenz {
  readonly id: string
  readonly title: string
  readonly kompetenzbereich: { readonly key: string; readonly label: string }
}

export interface LessonImpulseFilterOptions {
  readonly kompetenzen: ReadonlyArray<LessonImpulseFilterKompetenz>
  readonly grades: ReadonlyArray<number>
  readonly subjects: ReadonlyArray<LessonImpulseCodeName>
}

export function matchesLessonImpulseFilters(
  impulse: LessonImpulseSummary,
  filters: LessonImpulseFilters,
): boolean {
  if (
    filters.kompetenzIds.length > 0 &&
    !impulse.ueberfachlicheKompetenzen.some((kompetenz) =>
      filters.kompetenzIds.includes(kompetenz.id),
    )
  ) {
    return false
  }

  if (
    filters.grades.length > 0 &&
    !impulse.grades.some((grade) => filters.grades.includes(grade))
  ) {
    return false
  }

  if (
    filters.subjectCodes.length > 0 &&
    !impulse.subjects.some((subject) => filters.subjectCodes.includes(subject.code))
  ) {
    return false
  }

  if (filters.bookmarkedOnly && !impulse.bookmarkedAt) {
    return false
  }

  return true
}

export function lessonImpulseFilterOptions(
  impulses: ReadonlyArray<LessonImpulseSummary>,
): LessonImpulseFilterOptions {
  const kompetenzen = new Map<string, LessonImpulseFilterKompetenz>()
  const grades = new Set<number>()
  const subjects = new Map<string, LessonImpulseCodeName>()

  for (const impulse of impulses) {
    for (const kompetenz of impulse.ueberfachlicheKompetenzen) {
      if (!kompetenzen.has(kompetenz.id)) {
        kompetenzen.set(kompetenz.id, {
          id: kompetenz.id,
          title: kompetenz.title,
          kompetenzbereich: kompetenz.kompetenzbereich,
        })
      }
    }
    for (const grade of impulse.grades) {
      grades.add(grade)
    }
    for (const subject of impulse.subjects) {
      if (!subjects.has(subject.code)) {
        subjects.set(subject.code, {
          code: subject.code,
          name: subject.name.length > 10 ? subject.code : subject.name,
        })
      }
    }
  }

  return {
    kompetenzen: [...kompetenzen.values()].sort((a, b) => a.title.localeCompare(b.title, 'de')),
    grades: [...grades].sort((a, b) => a - b),
    subjects: [...subjects.values()].sort((a, b) => a.name.localeCompare(b.name, 'de')),
  }
}
